/**
 * ============================================================================
 * PRODUCTCARD - CONTRAT UI STRICT
 * ============================================================================
 * 
 * RÈGLE FONDAMENTALE:
 * - 1 seul composant ProductCard pour toute l'application
 * - 0 duplication autorisée
 * - 0 implémentation inline dans d'autres composants
 * - Variations uniquement via prop "variant"
 * 
 * VARIANTS AUTORISÉS:
 * - "default"  : Catalogue général, Partner pages (features complètes)
 * - "compact"  : CMS (Nouveautés, Précommandes) - densité réduite
 * - "featured" : Mise en avant (home, highlights) - taille plus grande
 * 
 * DATA CONTRACT:
 * Le composant DOIT recevoir un Product normalisé avec:
 * 
 * OBLIGATOIRES:
 * - id (string)
 * - intitule (string)
 * - visuels (array)
 * - tags (array)
 * - partner (object|null) avec { id, nom, slug, logo }
 * 
 * OPTIONNELS:
 * - nb_joueurs_min/max (number)
 * - age_minimum (number)
 * - temps_moyen_partie (number)
 * - nb_favoris (number)
 * - nb_a_suivre (number)
 * - prix_vente_conseille (number)
 * 
 * CHAMPS NON AFFICHÉS (disponibles backend mais masqués):
 * - editeur (supprimé de l'affichage)
 * - description_courte (supprimé de l'affichage)
 * 
 * CONTRAINTES STRICTES:
 * ❌ PAS de ProductCardV2 ou alternative
 * ❌ PAS d'implémentation inline
 * ❌ PAS de logique UI conditionnelle par page
 * ❌ PAS de fallback CSS spécifique par contexte
 * 
 * COMPORTEMENTS:
 * - Click product → /dashboard/produit/:id
 * - Click partner → /partner/:slug (lien BLEU)
 * - Favoris/Suivre → toggle API global
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Flag, Users, Clock, Building2, Calendar, Cake, Calculator, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import api from '../lib/api';
import TagList from './TagList';
import { getNormalizedTags } from '../lib/tagEngine';
import { RosetteIcon } from './PartnerBadges';
import { resolveProduct } from '../domain/product/resolveProduct';
import QuickAddModal from './QuickAddModal';
import { getEligibleDistributors } from '../lib/eligibility';
import { isPartnerClickable, PARTNER_ACTIVE_CLASSES, PARTNER_DISABLED_CLASSES, PARTNER_DISABLED_TOOLTIP } from '../lib/partnerPolicy';

// ============================================================================
// DATE AVAILABILITY BADGE - UI ONLY (no business logic)
// ============================================================================

/**
 * Vérifie si une date est valide et future/aujourd'hui
 * @param {string} dateStr - Date au format ISO
 * @returns {Object} - { isValid, isFutureOrToday, formattedDate }
 */
const parseDateForDisplay = (dateStr) => {
  // Cas invalides : null, undefined, chaîne vide
  if (!dateStr || dateStr === '') {
    return { isValid: false, isFutureOrToday: false, formattedDate: null };
  }
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return { isValid: false, isFutureOrToday: false, formattedDate: null };
    }
    
    // Comparer avec aujourd'hui (début de journée)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    
    const isFutureOrToday = dateOnly >= today;
    
    // Formater en JJ/MM/AAAA
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;
    
    return { isValid: true, isFutureOrToday, formattedDate };
  } catch {
    return { isValid: false, isFutureOrToday: false, formattedDate: null };
  }
};

/**
 * DateAvailabilityBadge - Bannière de date de disponibilité
 * 
 * SOURCE UNIQUE DE VÉRITÉ UI pour l'affichage de la date de disponibilité
 * Aucune logique métier de filtrage - UI ONLY
 * 
 * RÈGLES D'AFFICHAGE:
 * - Date future ou aujourd'hui → "📅 Disponible le JJ/MM/AAAA"
 * - Date absente/invalide     → "📅 Date de sortie inconnue"
 * - Date passée               → N'AFFICHE RIEN
 * 
 * @param {string} dateSortie - Date de sortie (ISO format)
 * @param {string} variant - Variant de la card (default, compact, featured)
 */
const DateAvailabilityBadge = ({ dateSortie, variant = 'default' }) => {
  const { isValid, isFutureOrToday, formattedDate } = parseDateForDisplay(dateSortie);
  
  // Configuration responsive par variant
  const BADGE_CONFIG = {
    default: {
      fontSize: 'text-xs sm:text-sm',
      padding: 'px-3 py-1.5',
      iconSize: 'w-3.5 h-3.5'
    },
    compact: {
      fontSize: 'text-[10px] sm:text-xs',
      padding: 'px-2 py-1',
      iconSize: 'w-3 h-3'
    },
    featured: {
      fontSize: 'text-sm sm:text-base',
      padding: 'px-4 py-2',
      iconSize: 'w-4 h-4'
    }
  };
  
  const config = BADGE_CONFIG[variant] || BADGE_CONFIG.default;
  
  // CAS 1: Date passée → NE RIEN AFFICHER
  if (isValid && !isFutureOrToday) {
    return null;
  }
  
  // CAS 2: Date absente/invalide → "Date de sortie inconnue"
  // CAS 3: Date future/aujourd'hui → "Disponible le JJ/MM/AAAA"
  const displayText = isValid && formattedDate 
    ? `Disponible le ${formattedDate}`
    : 'Date de sortie inconnue';
  
  return (
    <div 
      className={`
        absolute bottom-0 left-0 right-0
        flex items-center justify-center gap-1.5
        ${config.padding}
        bg-black/50 backdrop-blur-[2px]
        text-white font-medium
        ${config.fontSize}
      `}
      data-testid="date-availability-badge"
    >
      <Calendar className={config.iconSize} />
      {displayText}
    </div>
  );
};

// ============================================================================
// PRODUCT CARD ADD BUTTON — Opens QuickAddModal (or shows ineligible label)
// ============================================================================
const ProductCardAddButton = ({ product }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const eligibleDists = getEligibleDistributors(product);

  if (eligibleDists.length === 0) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100">
        <p className="text-center text-[10px] text-slate-400 py-1" data-testid={`no-commercial-${product.id}`}>
          Pas de distributeur partenaire commercial
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" onClick={e => e.preventDefault()}>
      <button
        onClick={e => { e.preventDefault(); e.stopPropagation(); setModalOpen(true); }}
        className="w-full h-8 flex items-center justify-center gap-1.5 text-[11px] font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-all duration-200"
        data-testid={`quick-add-${product.id}`}
      >
        <Calculator className="h-3.5 w-3.5" /> Ajouter
      </button>
      {modalOpen && <QuickAddModal product={product} open={modalOpen} onClose={() => setModalOpen(false)} />}
    </div>
  );
};


/**
 * ProductCard Component
 * 
 * @param {Object} product - Product normalisé (voir DATA CONTRACT ci-dessus)
 * @param {string} variant - "default" | "compact" | "featured"
 * @param {boolean} showTag - Afficher le tag (default: true)
 */
const ProductCard = ({ 
  product, 
  variant = 'default',
  showTag = true 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================================================================
  // FAVORIS STATE
  // ============================================================================
  useEffect(() => {
    checkFavoriteStatus();
  }, [product.id, user]);

  const checkFavoriteStatus = async () => {
    if (!user) return;
    
    try {
      const response = await api.get(`/favorites?user_id=${user.id}&product_id=${product.id}`);
      const favs = response.data || [];
      setIsFavorite(favs.some(f => f.type === 'favori'));
      setIsFollowing(favs.some(f => f.type === 'a_suivre'));
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const toggleFavorite = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user || loading) return;
    
    setLoading(true);
    try {
      const isCurrentlyActive = (type === 'favori' && isFavorite) || (type === 'a_suivre' && isFollowing);
      
      if (isCurrentlyActive) {
        await api.delete(`/favorites?user_id=${user.id}&product_id=${product.id}&type=${type}`);
        if (type === 'favori') setIsFavorite(false);
        if (type === 'a_suivre') setIsFollowing(false);
      } else {
        await api.post('/favorites', {
          user_id: user.id,
          product_id: product.id,
          type: type
        });
        if (type === 'favori') setIsFavorite(true);
        if (type === 'a_suivre') setIsFollowing(true);
      }
      
      await checkFavoriteStatus();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // TAGS - Using TAG ENGINE FINAL LOCK (getNormalizedTags)
  // NOTE: Backend garantit que 'date_sortie_inconnue' n'est jamais envoyé
  // ============================================================================
  const productTags = getNormalizedTags(product);

  // ============================================================================
  // VARIANT STYLES (STRICT - seules variations autorisées)
  // ============================================================================
  const VARIANT_CONFIG = {
    default: {
      container: 'ludo-card-warm hover:-translate-y-1',
      imageAspect: 'aspect-[4/3]',
      padding: 'p-4',
      titleSize: 'text-sm font-bold',
      showGameInfo: true,
      showSocialCounts: true,
      buttonSize: 'p-2',
      iconSize: 'w-4 h-4'
    },
    compact: {
      container: 'ludo-card-warm hover:-translate-y-0.5',
      imageAspect: 'aspect-[3/2]',
      padding: 'p-3',
      titleSize: 'text-sm font-bold',
      showGameInfo: true,
      showSocialCounts: true,
      buttonSize: 'p-1.5',
      iconSize: 'w-3.5 h-3.5'
    },
    featured: {
      container: 'ludo-card-warm border-2 rounded-xl hover:-translate-y-2',
      imageAspect: 'aspect-[4/3]',
      padding: 'p-5',
      titleSize: 'text-lg font-bold',
      showGameInfo: true,
      showSocialCounts: true,
      buttonSize: 'p-2.5',
      iconSize: 'w-5 h-5'
    }
  };

  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.default;

  // ============================================================================
  // PRODUCT DOMAIN RESOLVER — Single Source of Truth (Phase 4B Clean)
  // ============================================================================
  const resolved = resolveProduct(product);
  const rd = resolved.distributors;
  const rp = resolved.pricing;

  if (typeof window !== 'undefined' && window.__PRODUCT_RESOLVER_DEBUG__) {
    console.log('[RESOLVER_CLEAN]', { productId: product.id, resolved });
  }

  // ============================================================================
  // PARTNER CLICK HANDLER — ACCESS RULE PARTNER (via shared helper)
  // ============================================================================
  const handlePartnerClick = (e) => {
    const slug = rd.primary?.slug || product.partner?.slug;
    if (slug && isPartnerClickable(rd.primary || product.partner || {})) {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/partner/${slug}`);
    }
  };

  const partnerIsClickable = isPartnerClickable(rd.primary || product.partner || {});

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Link
      to={`/dashboard/produit/${product.id}`}
      className={`group block ${config.container}`}
      data-testid={`product-card-${product.id}`}
      data-variant={variant}
    >
      {/* IMAGE SECTION - Ratio constant par variant + Content Fit System */}
      <div className={`${config.imageAspect} bg-white border-b border-black/[0.06] relative flex items-center justify-center overflow-hidden`}>
        {product.visuels && product.visuels.length > 0 ? (
          <img 
            src={product.visuels[0]} 
            alt={product.intitule} 
            className="max-w-full max-h-full object-contain transition-transform group-hover:scale-105 p-2"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            Pas d&apos;image
          </div>
        )}
        
        {/* DATE AVAILABILITY BADGE - UI Only */}
        <DateAvailabilityBadge 
          dateSortie={product.date_sortie_iso}
          variant={variant}
        />
        
        {/* FAVORIS BUTTONS - Même comportement partout */}
        <div className="absolute top-2 right-2 flex gap-1.5">
          <button
            onClick={(e) => toggleFavorite(e, 'favori')}
            className={`${config.buttonSize} rounded-full backdrop-blur-md transition-all shadow-md hover:scale-110 ${
              isFavorite 
                ? 'bg-red-500 text-white' 
                : 'bg-white/90 text-gray-600 hover:bg-white'
            }`}
            data-testid={`favorite-button-${product.id}`}
            title={isFavorite ? 'Retirer de la Wishlist' : 'Ajouter à la Wishlist'}
          >
            <Heart className={`${config.iconSize} ${isFavorite ? 'fill-current' : ''}`} />
          </button>
          
          <button
            onClick={(e) => toggleFavorite(e, 'a_suivre')}
            className={`${config.buttonSize} rounded-full backdrop-blur-md transition-all shadow-md hover:scale-110 ${
              isFollowing 
                ? 'bg-blue-500 text-white' 
                : 'bg-white/90 text-gray-600 hover:bg-white'
            }`}
            data-testid={`follow-button-${product.id}`}
            title={isFollowing ? 'Ne plus suivre' : 'Suivre ce produit'}
          >
            <Flag className={`${config.iconSize} ${isFollowing ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>

      {/* CONTENT SECTION - V4.7 Reorder + Centering */}
      <div className={config.padding}>
        {/* 1. TITLE */}
        <h3 className={`${config.titleSize} line-clamp-2 mb-2`}>
          {product.intitule ? product.intitule : (
            <span className="text-muted-foreground italic">Produit incomplet</span>
          )}
        </h3>
        
        {/* 2. DISTRIBUTEUR — Resolver Clean + ACCESS RULE PARTNER */}
        {rd.mode !== 'none' ? (
          <div className="flex items-center gap-1.5 mb-2">
            <Building2 className="h-3 w-3 flex-shrink-0 text-blue-600" />
            {rd.mode === 'multi' ? (
              <span className="text-xs text-blue-600 font-medium truncate">{rd.displayLabel}</span>
            ) : rd.primary?.slug && partnerIsClickable ? (
              <button
                onClick={handlePartnerClick}
                className={`text-xs font-medium truncate ${PARTNER_ACTIVE_CLASSES}`}
                title={`Voir le partenaire ${rd.primary.nom}`}
                data-testid={`partner-link-${product.id}`}
              >
                {rd.primary.nom}
              </button>
            ) : rd.primary?.slug ? (
              <span
                className={`text-xs font-medium truncate ${PARTNER_DISABLED_CLASSES}`}
                title={PARTNER_DISABLED_TOOLTIP}
                aria-disabled="true"
                data-testid={`partner-disabled-${product.id}`}
              >
                {rd.primary.nom}
              </span>
            ) : (
              <span className="text-xs text-blue-600 truncate">{rd.displayLabel}</span>
            )}
            {rd.primary?.is_partner === true && <RosetteIcon className="w-4 h-4 flex-shrink-0" />}
          </div>
        ) : null}
        
        {/* 3. PRICING — PVC comme prix de référence produit */}
        <div className="flex items-center justify-between mb-3">
          {product.prix_vente_conseille != null && product.prix_vente_conseille > 0 ? (
            <span className="inline-flex items-baseline gap-1">
              <span className="font-bold text-primary text-xl" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid={`product-price-${product.id}`}>
                {Number(product.prix_vente_conseille).toFixed(2)} €
              </span>
              <span className="text-xs text-muted-foreground">PVC</span>
            </span>
          ) : (
            <span className="text-muted-foreground text-sm italic" data-testid={`product-price-nc-${product.id}`}>Prix NC</span>
          )}
        </div>
        
        {/* ═══ CENTERED ZONE (everything below pricing) ═══ */}
        <div className="text-center">
          {/* 4. GAMEPLAY — monochrome noir, centré */}
          {config.showGameInfo && (product.nb_joueurs_min || product.age_minimum || product.temps_moyen_partie) && (
            <div className="flex flex-wrap items-center justify-center gap-1.5 mb-2">
              {(product.nb_joueurs_min || product.nb_joueurs_max) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                  <Users className="h-3 w-3" />
                  {product.nb_joueurs_min === product.nb_joueurs_max 
                    ? product.nb_joueurs_min
                    : `${product.nb_joueurs_min || '?'}-${product.nb_joueurs_max || '?'}`}
                </span>
              )}
              {product.age_minimum && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                  <Cake className="h-3 w-3" />
                  {product.age_minimum}+
                </span>
              )}
              {product.temps_moyen_partie && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                  <Clock className="h-3 w-3" />
                  {product.temps_moyen_partie}&apos;
                </span>
              )}
            </div>
          )}
          
          {/* 5. TAGS SYSTÈME — centrés */}
          {showTag && productTags.length > 0 && (
            <div className="mb-2 flex justify-center">
              <TagList tags={productTags} variant="card" maxVisible={2} />
            </div>
          )}
        </div>
        
        {/* SOCIAL COUNTS — position inchangée, centré dans son bloc */}
        {config.showSocialCounts && (product.nb_favoris > 0 || product.nb_a_suivre > 0) && (
          <div className="flex items-center justify-center gap-3 pt-2 border-t text-xs text-muted-foreground">
            {product.nb_favoris > 0 && (
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3 text-red-400" />
                {product.nb_favoris}
              </span>
            )}
            {product.nb_a_suivre > 0 && (
              <span className="flex items-center gap-1">
                <Flag className="h-3 w-3 text-blue-400" />
                {product.nb_a_suivre}
              </span>
            )}
          </div>
        )}

        {/* QUICK ADD — Single button opens QuickAddModal */}
        {rp.hasAnyPrice && user?.role === 'retailer' && <ProductCardAddButton product={product} />}
      </div>
    </Link>
  );
};

// ============================================================================
// EXPORT - UNIQUE COMPONENT
// ============================================================================
export default ProductCard;

// ============================================================================
// VARIANT TYPES (pour documentation TypeScript-like)
// ============================================================================
/**
 * @typedef {'default' | 'compact' | 'featured'} ProductCardVariant
 * 
 * @typedef {Object} ProductCardProduct
 * @property {string} id - ID unique du produit
 * @property {string} intitule - Nom du produit
 * @property {string[]} visuels - URLs des images
 * @property {string[]} tags - Tags du produit
 * @property {Object|null} partner - Partenaire enrichi
 * @property {string} [partner.id] - ID du partenaire
 * @property {string} [partner.nom] - Nom du partenaire
 * @property {string} [partner.slug] - Slug pour navigation
 * @property {string} [partner.logo] - URL du logo
 * @property {number} [nb_joueurs_min] - Nombre minimum de joueurs
 * @property {number} [nb_joueurs_max] - Nombre maximum de joueurs
 * @property {number} [age_minimum] - Âge minimum
 * @property {number} [temps_moyen_partie] - Durée moyenne en minutes
 * @property {number} [nb_favoris] - Nombre de favoris
 * @property {number} [nb_a_suivre] - Nombre de suivis
 * @property {number} [prix_vente_conseille] - Prix de vente conseillé
 * @property {string} [date_sortie_iso] - Date de sortie ISO (prioritaire)
 * @property {string} [date_sortie] - Date de sortie (fallback)
 * 
 * CHAMPS NON AFFICHÉS (disponibles mais masqués):
 * @property {string} [editeur] - Nom de l'éditeur (non affiché)
 * @property {string} [description_courte] - Description courte (non affichée)
 */
